<template>
<v-row class="mt-0 mb-4" size="1" :gap="$vuetify?.display?.width <= 1024 ? 6 : null">
    <v-col class="align-self-start">
        <VueDatePicker 
            v-bind="{ ...props, ...$attrs }" 
            ref="internalRef"
            :model-value="modelValue" 
            @update:model-value="onUpdate"
            :text-input="textInputOptions"
            :enableTimePicker="false" 
            :clearable="false" 
            :disabled="props.disabled"
            autocomplete="off"
            :teleport="true"
            model-type="iso"
            @open="handleOpen"
            @paste="$event => handlePaste($event)"
            @closed="handleBlur"
        >
            <template
                #dp-input="{ value, onInput, onEnter, onTab, onBlur, onKeypress, toggleMenu, isMenuOpen }">
                <v-text-field 
                    v-bind="{ ...props, ...$attrs }" 
                    :clearable="false" 
                    autocomplete="suppress"
                    :label="datepickerLabel"
                    :model-value="value" 
                    :variant="$attrs?.variant ?? 'underlined'" 
                    :hint="$attrs?.hint ?? null"
                    :persistent-hint="$attrs?.persistentHint ?? $attrs?.hint?.length > 0 ?? false"
                    :hide-details="!$attrs.hint?.length > 0"
                    @update:model-value="onInput" 
                    @focus="handleFocus(isMenuOpen, toggleMenu)"
                    @keyup:enter="onEnter"
                    @keydown="handleKeypress($event, onKeypress)"
                >
                    <template v-slot:append-inner v-if="!props.readonly && !props.disabled && !props.focusTrigger">
                        <v-icon icon="mdi-calendar" @click="toggleMenu" tabindex="-1"/>
                    </template>
                    <template v-slot:append v-if="props.mode !== 'datetime' && props.clearable && modelValue">
                        <v-icon icon="mdi-close-circle" @click.stop="modelValue = null" tabindex="-1"/>
                    </template>
                </v-text-field>
            </template>
            <template #action-preview="{ value }">
                <template v-if="value">
                    <template v-if="props.mode === 'datetime'">
                        {{ moment(value).format('lll') }}
                    </template>
                    <template v-else>
                        {{ moment(value).format('ll') }}
                    </template>
                </template>
                <template v-else>
                    Choose a date
                </template>
            </template>
        </VueDatePicker>
    </v-col>
    <v-col v-if="props.mode === 'datetime'" class="align-self-start">
        <v-combobox 
            ref="timepicker"
            autocomplete="suppress"
            v-bind="{...$attrs}"
            v-model="timePart" 
            :clearable="false"
            :label="timepickerLabel"
            :variant="$attrs?.variant ?? 'underlined'" 
            :item-props="isDisabled"
            expanded
            :items="timeOptions" 
            :filter-keys="['title']" 
            filter-mode="some"
            hide-details
            :rules="timeRules" 
            :disabled="!datePart || props.disabled" 
            :append-icon="props.clearable && modelValue ? 'mdi-close-circle' : null"
            @click:append="modelValue = null" 
            @paste="$event => handlePaste($event)" 
        />
    </v-col>
</v-row>
</template>
<script setup>
import { ref, computed, useAttrs, nextTick } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import moment from 'moment';
import { useGlobalState } from '@/stores/globalState';

const globalState = useGlobalState();

const props = defineProps({
    mode: {
        type: String,
        default() {
            return 'datetime';
        }
    },
    focusTrigger: {
        type: Boolean,
        default: true
    },
    readonly: {
        type: Boolean,
        default() {
            return false;
        }
    },
    disabled: {
        type: Boolean,
        default() {
            return false;
        }
    },
    clearable: {
        type: Boolean,
        default() {
            return false;
        }
    },
    defaultHour: {
        type: Number,
        default: 13
    },
});

const textInputOptions = computed(() => {
    if (!props.focusTrigger) {
        return { openMenu: false };
    }
    return { enterSubmit: true, tabSubmit: true, openMenu: 'open' };
});
const emit = defineEmits(['closed']);
const modelValue = defineModel();
const $attrs = useAttrs();

// The interval to use to generate the time dropdown options
const interval = 15;
// const timezone = ref({ exactMatch: false, dateInTz: true, convertModel: true });
const timepicker = ref();
const internalRef = ref();

const timeOptions = computed(() => {
    const times = []
    // 00:00 of today
    const start = moment().startOf('day');
    // 00:00 of tomorrow
    const end = start.clone().add(1, 'day');

    for (let m = start.clone(); m.isBefore(end); m.add(interval, 'minutes')) {
        times.push(m.format('h:mm a'));
    }
    return times;
});
const datepickerLabel = computed(() => {
    if ($attrs.label) {
        if (Array.isArray($attrs.label)) {
            return $attrs.label?.[0] ?? 'Date';
        }
        if (!$attrs.label.toLowerCase().includes('date')) {
            return `${$attrs.label} Date`;
        }
        return $attrs.label;
    }
    return 'Date';
});
const timepickerLabel = computed(() => {
    if ($attrs.label) {
        if (Array.isArray($attrs.label)) {
            return $attrs.label?.[1] ?? 'Time';
        }
        if (!$attrs.label.toLowerCase().includes('time')) {
            return `${$attrs.label.replace(/date/im, '')} Time`;
        }
        return $attrs.label;
    }
    return 'Time';
});
const timeRules = [
    (input) => {
        const timeReg = /([0-1]?[0-9]|2[0-3]):[0-5][0-9]\s?([AaPp][Mm])/gm;
        return timeReg.test(input);
    },
    (input) => {
        if (props.mode === 'datetime' && $attrs['min-date']) {
            const minDateObject = moment($attrs['min-date']);
            if (minDateObject.isAfter(moment(`${datePart.value} ${input}`))) {
                return false;
            }
        }
        return true;
    }
]
// Split the ISO string into its two parts
function parseDate(inputDate) {
    if (!inputDate) {
        return ['', ''];
    }
    // console.log('parseDate', inputDate);
    let parsedDate;
    if (typeof inputDate === 'string' && inputDate.includes('T')) {
        if (inputDate.endsWith('Z')) {
            parsedDate = moment(inputDate);
        } else {
            parsedDate = moment.utc(inputDate).local();
        }
    } else if (moment.isMoment(inputDate) && inputDate.isUTC()) {
        parsedDate = inputDate.local();
    } else {
        parsedDate = moment(inputDate);
    }
    const [date, time] = [
        parsedDate.format('YYYY-MM-DD'),
        parsedDate.format('h:mm a')
    ];
    return [date, time];
}

const datePart = computed({
    get() {
        return parseDate(modelValue.value)[0];
    },
    set(newDate) {
        // console.log('datePart setter', newDate);
        if (!newDate) return;

        const newLocal = moment(newDate);
        let time = { hours: props.defaultHour, minutes: 0, seconds: 0, milliseconds: 0 };
        // overwrite if necessary
        if (modelValue.value) {
            const prev = moment(modelValue.value);
            time = { hours: prev.hours(), minutes: prev.minutes(), seconds: prev.seconds(), milliseconds: 0 };
        }

        modelValue.value = newLocal.clone().set(time).toISOString();
    },
});

// The time part – HH:mm
const timePart = computed({
    get() {
        const t = parseDate(modelValue.value)[1];
        return t || null;
    },
    set(newTime) {
        if (typeof newTime === 'string') {

            const timeReg = /([0-1]?[0-9]|2[0-3]):[0-5][0-9]\s?([AaPp][Mm])/gm;
            if (!timeReg.test(newTime)) {
                return false;
            }
            const isAm = newTime.toLowerCase().includes('am');
            const [hours, mins] = newTime.replace(/\s?([AaPp][Mm])/gm, '').split(':');
            const hourInt = parseInt(hours);
            let tmpTime = {
                // If PM and not 12, we add 12 since the object expects 24 hour time, so 3 = 15, etc
                hours: isAm ? hourInt : hourInt !== 12 ? hourInt + (12) : hourInt,
                minutes: parseInt(mins),
                seconds: 0
            }
            newTime = tmpTime;

        }

        const oldDate = moment(modelValue.value);
        const newDate = oldDate.clone().set(newTime);

        modelValue.value = newDate?.toISOString() || null;
    },
});

function treatInput(input = '') {
    if (input?.length && !input.endsWith('Z')) {
        input = input.replace('-', ',');
    }
    return input;
}
function handlePaste(event) {
    const pastedText = treatInput(event.clipboardData.getData('text/plain'));
    let parsedDate = new Date(pastedText);
    event.preventDefault();

    if (!isNaN(parsedDate)) {
        modelValue.value = parsedDate.toISOString();
    } else {
        globalState.showSnackbar({ message: 'Date in clipboard could not be parsed', color: 'error' });
    }
}

async function handleBlur() {
    emit('closed');
}

function handleKeypress($event, callback) {
    // Tabbing from the appended icon causes an error within vue-datepicker, this worksaround that issue
    if ($event.target.tagName !== 'I') {
        callback($event);
    }
}
async function handleOpen() {
    if (!modelValue.value) {
        await nextTick();
        internalRef.value.updateInternalModelValue(moment().hours(props.defaultHour).minutes(0).seconds(0).milliseconds(0).toDate());
    }
}
async function onUpdate(updatedValue) {
    // console.log(updatedValue);
    datePart.value = updatedValue;
    if (props.mode === 'datetime' && modelValue.value) {
        // await nextTick();
        // console.log(timepicker.value);
        timepicker.value.menu = true;
    }
}
const isDisabled = (item) => {
    if (props.mode === 'datetime' && $attrs['min-date']) {
        const minDateObject = moment($attrs['min-date']);
        const [minDay] = (moment.isMoment($attrs['min-date']) ? $attrs['min-date'].toISOString() : moment($attrs['min-date']).toISOString()).split('T');
        if (minDay > datePart.value) {
            return {
                disabled: true
            }
        } else if (minDay === datePart.value) {
            // Selected date is same as minDate , so we need to disable spcific times
            if (minDateObject.isAfter(moment(`${datePart.value} ${item}`))) {
                return {
                    disabled: true
                }
            }
        }
        // Only check if datePart is equal to or less than minDate
        return {
            disabled: false, //item.value <= now.value}
        }
    }
    return {};
}
function handleFocus(isMenuOpen, toggleMenu) {
    if (props.focusTrigger) {
        if (!isMenuOpen) {
            toggleMenu();
        }
    }
}
function open() {
    internalRef.value?.openMenu?.();
}
defineExpose({
    open
});
</script>